import { Criterion, RankingMode, SavedTournament, Standings } from "../types.ts";
import {
  ResultMatrix,
  calculateBuchholz,
  calculatePoints,
  combineRounds,
} from "./tournament-calc.ts";

/**
 * Pořadí při rovnosti bodů.
 *
 * Kritéria se vyhodnocují shora dolů – první rozdíl rozhoduje a zbytek
 * se na danou skupinu hráčů už nepoužije. Změna pořadí kritérií je proto
 * změnou jediného řádku v `MODE_CRITERIA`.
 */
export const MODE_CRITERIA: { [key in RankingMode]: Criterion[] } = {
  // Kavárna: vzájemný zápas, pak dřívější příchod, nakonec Sonneborn-Berger
  cafe: ["points", "h2h", "arrival", "sb"],
  // FIDE, turnaj každý s každým: vzájemný zápas, Sonneborn-Berger,
  // Koya systém, počet výher
  fide: ["points", "h2h", "sb", "koya", "wins"],
  // Volitelný: zatím nespecifikováno, rozhodují pouze body
  custom: ["points"],
};

/** Pomocná hodnocení, která se v daném režimu zobrazují vždy. */
const MODE_ALWAYS_VISIBLE: { [key in RankingMode]: Criterion[] } = {
  cafe: [],
  fide: ["sb"],
  custom: [],
};

/** Kritéria, která mají v tabulce vlastní číselný sloupec. */
const COLUMN_CRITERIA: Criterion[] = ["sb", "koya", "wins"];

export const CRITERION_LABELS: {
  [key in Criterion]: { short: string; full: string };
} = {
  points: { short: "Body", full: "Body" },
  h2h: { short: "VZ", full: "Vzájemný zápas" },
  arrival: { short: "Př.", full: "Pořadí příchodu (pozice v tabulce)" },
  sb: { short: "SB", full: "Sonneborn-Berger" },
  koya: { short: "Koya", full: "Koya systém" },
  wins: { short: "V", full: "Počet výher" },
};

export const RANKING_MODES: {
  id: RankingMode;
  label: string;
  description: string;
}[] = [
  {
    id: "cafe",
    label: "Kavárna",
    description:
      "Při rovnosti bodů rozhoduje vzájemný zápas, pak dřívější příchod (pozice v tabulce) a nakonec Sonneborn-Berger. Pomocná hodnocení se v tabulce ukážou, jen když o pořadí opravdu rozhodnou.",
  },
  {
    id: "fide",
    label: "Turnaj (FIDE)",
    description:
      "Hodnocení podle pravidel FIDE pro turnaj každý s každým: vzájemný zápas, Sonneborn-Berger, Koya systém a počet výher.",
  },
  {
    id: "custom",
    label: "Volitelný",
    description:
      "Zatím nespecifikováno – rozhodují pouze body a hráči se stejným počtem bodů sdílejí pořadí.",
  },
];

export const DEFAULT_RANKING_MODE: RankingMode = "cafe";

/** Bezpečný převod uložené/neznámé hodnoty na režim hodnocení. */
export const resolveRankingMode = (value: unknown): RankingMode =>
  value === "cafe" || value === "fide" || value === "custom"
    ? value
    : DEFAULT_RANKING_MODE;

/**
 * Sonneborn-Berger: skóre proti soupeři × body soupeře.
 *
 * Zápis přes násobení (místo větvení na výhru/remízu) je důležitý
 * u dvoukolových turnajů, kde má buňka i hodnoty 1,5 nebo 2.
 */
const sonnebornBerger = (
  matrix: ResultMatrix,
  playerCount: number
): number[] => {
  const points = calculatePoints(matrix, playerCount);
  const scores: number[] = [];

  for (let i = 0; i < playerCount; i++) {
    let score = 0;
    for (let j = 0; j < playerCount; j++) {
      if (i === j) continue;
      const value = matrix[i]?.[j];
      if (value !== null && value !== undefined) {
        score += value * points[j];
      }
    }
    scores.push(score);
  }

  return scores;
};

/**
 * Koya systém: body získané proti soupeřům, kteří dosáhli
 * alespoň 50 % z možných bodů.
 */
const koya = (
  matrix: ResultMatrix,
  playerCount: number,
  pointsPerPair: number
): number[] => {
  const points = calculatePoints(matrix, playerCount);
  const threshold = ((playerCount - 1) * pointsPerPair) / 2;
  const scores: number[] = [];

  for (let i = 0; i < playerCount; i++) {
    let score = 0;
    for (let j = 0; j < playerCount; j++) {
      if (i === j || points[j] < threshold) continue;
      const value = matrix[i]?.[j];
      if (value !== null && value !== undefined) {
        score += value;
      }
    }
    scores.push(score);
  }

  return scores;
};

/**
 * Počet vyhraných partií. Počítá se přes jednotlivá kola, ne přes
 * součtovou matici – jinak by dvoukolová výhra 1:1 vypadala jako výhra.
 */
const winCount = (rounds: ResultMatrix[], playerCount: number): number[] => {
  const wins: number[] = [];

  for (let i = 0; i < playerCount; i++) {
    let total = 0;
    for (const round of rounds) {
      for (let j = 0; j < playerCount; j++) {
        if (i !== j && round[i]?.[j] === 1) total += 1;
      }
    }
    wins.push(total);
  }

  return wins;
};

/**
 * Vzájemné zápasy uvnitř skupiny hráčů se stejným počtem bodů.
 *
 * Podle FIDE se kritérium použije jen tehdy, když spolu všichni hráči
 * skupiny odehráli všechny partie – jinak vrací null a přeskakuje se.
 */
const headToHead = (
  matrix: ResultMatrix,
  group: number[]
): { [key: number]: number } | null => {
  const scores: { [key: number]: number } = {};

  for (const i of group) {
    let score = 0;
    for (const j of group) {
      if (i === j) continue;
      const value = matrix[i]?.[j];
      if (value === null || value === undefined) {
        return null;
      }
      score += value;
    }
    scores[i] = score;
  }

  return scores;
};

/**
 * Kompletní pořadí podle zvoleného režimu.
 *
 * Hráči, které nerozhodne žádné kritérium, sdílejí stejné pořadí.
 * Indexy odpovídají pozicím v předané matici – u turnaje s neobsazenými
 * místy se tedy volá nad ořezanou tabulkou (viz `cropMatrix`).
 *
 * @param rounds jednotlivá kola ([matice] nebo [matice, odvety])
 */
export const computeStandings = (
  rounds: ResultMatrix[],
  playerCount: number,
  mode: RankingMode
): Standings => {
  const criteria = MODE_CRITERIA[mode] ?? MODE_CRITERIA[DEFAULT_RANKING_MODE];
  const pointsPerPair = Math.max(1, rounds.length);

  const matrix = rounds.reduce<ResultMatrix>(
    (acc, round, index) =>
      index === 0 ? round : combineRounds(acc, round, playerCount),
    []
  );

  const points = calculatePoints(matrix, playerCount);
  const sb = sonnebornBerger(matrix, playerCount);
  const koyaScores = koya(matrix, playerCount, pointsPerPair);
  const wins = winCount(rounds, playerCount);

  const usedCriteria: Criterion[] = [];

  /** Hodnoty kritéria pro danou skupinu; null = kritérium nelze použít. */
  const valuesFor = (
    criterion: Criterion,
    group: number[]
  ): { [key: number]: number } | null => {
    if (criterion === "h2h") {
      return headToHead(matrix, group);
    }

    const source: { [key: string]: number[] } = {
      points,
      sb,
      koya: koyaScores,
      wins,
    };
    const values: { [key: number]: number } = {};

    for (const i of group) {
      // Dřívější příchod = nižší pozice v tabulce = lepší pořadí
      values[i] = criterion === "arrival" ? -i : source[criterion]?.[i] ?? 0;
    }

    return values;
  };

  /** Rozdělí skupinu na uspořádané podskupiny podle kritérií od `level`. */
  const split = (group: number[], level: number): number[][] => {
    if (group.length <= 1 || level >= criteria.length) {
      return [group];
    }

    const criterion = criteria[level];
    const values = valuesFor(criterion, group);

    if (!values) {
      return split(group, level + 1);
    }

    const distinct: number[] = [];
    group.forEach((i) => {
      if (distinct.indexOf(values[i]) === -1) distinct.push(values[i]);
    });

    if (distinct.length <= 1) {
      return split(group, level + 1);
    }

    // Body jsou základní seřazení, ne rozhodující kritérium
    if (level > 0 && usedCriteria.indexOf(criterion) === -1) {
      usedCriteria.push(criterion);
    }

    return distinct
      .sort((a, b) => b - a)
      .map((value) => group.filter((i) => values[i] === value))
      .reduce<number[][]>(
        (acc, bucket) => acc.concat(split(bucket, level + 1)),
        []
      );
  };

  const all: number[] = [];
  for (let i = 0; i < playerCount; i++) all.push(i);

  const rankings: { [key: number]: number } = {};
  let position = 1;
  split(all, 0).forEach((bucket) => {
    bucket.forEach((index) => {
      rankings[index] = position;
    });
    position += bucket.length;
  });

  const alwaysVisible = MODE_ALWAYS_VISIBLE[mode] ?? [];
  const columns = criteria.filter(
    (criterion) =>
      COLUMN_CRITERIA.indexOf(criterion) !== -1 &&
      (alwaysVisible.indexOf(criterion) !== -1 ||
        usedCriteria.indexOf(criterion) !== -1)
  );

  return {
    points,
    sb,
    koya: koyaScores,
    wins,
    rankings,
    usedCriteria,
    columns,
  };
};

/** Hodnota pomocného hodnocení pro výpis ve sloupci tabulky. */
export const criterionValue = (
  standings: Pick<Standings, "sb" | "koya" | "wins">,
  criterion: Criterion,
  index: number
): string => {
  if (criterion === "wins") {
    return standings.wins[index] !== undefined
      ? String(standings.wins[index])
      : "";
  }
  const source = criterion === "koya" ? standings.koya : standings.sb;
  return source[index] !== undefined ? source[index].toFixed(1) : "";
};

/** Popis jednoho sloupce pomocného hodnocení v tabulce archivu. */
export interface ArchiveColumn {
  key: string;
  short: string;
  full: string;
  values: number[];
  decimals: number;
}

/**
 * Sloupce pomocných hodnocení pro uložený turnaj.
 *
 * Turnaje uzavřené před zavedením režimů hodnocení režim uložený nemají –
 * ty se ukazují původně (PH1 = Buchholz, PH2 = Sonneborn-Berger), aby se
 * zpětně neměnilo, co u nich odjakživa svítilo.
 */
export const getArchiveColumns = (
  tournament: Pick<
    SavedTournament,
    "rankingMode" | "buchholz" | "sonneborn" | "koya" | "wins"
  >,
  rounds: ResultMatrix[],
  playerCount: number
): ArchiveColumn[] => {
  const mode = tournament.rankingMode;

  if (!mode) {
    const matrix = rounds.reduce<ResultMatrix>(
      (acc, round, index) =>
        index === 0 ? round : combineRounds(acc, round, playerCount),
      []
    );

    return [
      {
        key: "bh",
        short: "PH1",
        full: "Pomocné hodnocení 1 – Buchholz",
        values:
          tournament.buchholz && tournament.buchholz.length
            ? tournament.buchholz
            : calculateBuchholz(matrix, playerCount),
        decimals: 1,
      },
      {
        key: "sb",
        short: "PH2",
        full: "Pomocné hodnocení 2 – Sonneborn-Berger",
        values:
          tournament.sonneborn && tournament.sonneborn.length
            ? tournament.sonneborn
            : sonnebornBerger(matrix, playerCount),
        decimals: 1,
      },
    ];
  }

  const standings = computeStandings(rounds, playerCount, mode);

  return standings.columns.map((criterion) => ({
    key: criterion,
    short: CRITERION_LABELS[criterion].short,
    full: CRITERION_LABELS[criterion].full,
    values:
      criterion === "wins"
        ? standings.wins
        : criterion === "koya"
        ? standings.koya
        : standings.sb,
    decimals: criterion === "wins" ? 0 : 1,
  }));
};

/** Vypsání hodnoty sloupce archivu. */
export const formatArchiveValue = (
  column: ArchiveColumn,
  index: number
): string => {
  const value = column.values?.[index];
  return value === undefined || value === null
    ? ""
    : value.toFixed(column.decimals);
};
